import { FormControlLabel, MenuItem, Radio } from "@mui/material";
import { useCallback, useRef, useState } from "react";
import FptTrasladosFormContent from "../fptTraslados/fptTrasladosFormContent";
import FormikActions from "../../../commons/formik/formikActions";
import FormikAutocomplete from "../../../commons/formik/formikAutocomplete";
import FormikDateTimePicker from "../../../commons/formik/formikDateTimePicker";
import FormikForm from "../../../commons/formik/formikForm";
import FormikRadio from "../../../commons/formik/formikRadio";
import FormikSelect from "../../../commons/formik/formikSelect";
import FormikTextFieldSpeech from "../../../commons/formik/formikTextFieldSpeech";
import { StyledCommonForms } from "../../../styled/form";
import DropZone from "../dropZone/dropZone";
import FormSectionDivider from "../formSectionDivider";
import {
  FPT_LEGACY_ADDRESS_FIELDS,
  FPT_SKIP_REQUIRED_FIELDS,
  fptValidation,
  validateRequiredFields,
} from "../formValidation";
import DireccionFpt from "./direccionFpt";

const Fpt = ({
  handleClose,
  files,
  setFiles,
  handleSubmit,
  model,
  formValues,
  selectedDocument,
  idArt,
  disableDateUpdate,
  disabled,
  renderDirections,
  renderTrasladoExtras,
  hideEsperaRetorno,
  hideTipoTransporte,
  hideSolicitadoPor,
  extraValidate,
  validateOnChange = true,
  isolateFormFields = false,
}) => {
  const [attachmentError, setAttachmentError] = useState(false);
  const [direccionesHabitualesDesde, setDireccionesHabitualesDesde] = useState([
    ...model.fields.direccionesHabituales.options,
  ]);
  const [direccionesHabitualesHasta, setDireccionesHabitualesHasta] = useState([
    ...model.fields.direccionesHabituales.options,
  ]);

  const fields = model.fields;
  const fechaHoraSolicitudMinDate = fields.fechaHoraSolicitud.validations
    ? fields.fechaHoraSolicitud.validations.minDate
    : undefined;
  const extraValidateRef = useRef(extraValidate);
  extraValidateRef.current = extraValidate;
  const validateForm = useCallback(
    (values) => {
      // Se validan distintos campos requeridos dependiendo del tipo de FPT.
      const skipFields = [
        ...(values.puntos?.length
          ? [...FPT_LEGACY_ADDRESS_FIELDS, "tipoTransporteNombre"]
          : []),
        ...FPT_SKIP_REQUIRED_FIELDS,
      ];
      const errors = validateRequiredFields(values, fields, skipFields);
      if (model.configuration.requiresAttachment && files?.length === 0) {
        errors.attachment = "Adjunto requerido";
        setAttachmentError(true);
      }
      return { ...errors, ...extraValidateRef.current?.(values) };
    },
    [fields, files?.length, model.configuration.requiresAttachment],
  );
  return (
    <FormikForm
      artFields={fields}
      enableReinitialize
      initialValues={formValues}
      validationSchema={() => fptValidation(fechaHoraSolicitudMinDate)}
      validateOnChange={validateOnChange}
      validateOnBlur
      validate={validateForm}
      onSubmit={(values) => { handleSubmit(values) }}>
      {isolateFormFields ? (
        <FptTrasladosFormContent
          attachmentError={attachmentError}
          disableDateUpdate={disableDateUpdate}
          disabled={disabled}
          fechaHoraSolicitudMinDate={fechaHoraSolicitudMinDate}
          fields={fields}
          files={files}
          handleClose={handleClose}
          model={model}
          renderDirections={renderDirections}
          renderTrasladoExtras={renderTrasladoExtras}
          selectedDocument={selectedDocument}
          setAttachmentError={setAttachmentError}
          setFiles={setFiles}
        />
      ) : (
        ({ values, setFieldValue }) => (
          <StyledCommonForms.FormContainer>

            <StyledCommonForms.FieldContainer>
              <FormSectionDivider title="Traslado" />
              <StyledCommonForms.RowContainer>
                <StyledCommonForms.LeftColumnContainer>
                  <FormikDateTimePicker
                    name="fechaHoraSolicitud"
                    label={`Solicitado el ${fields.fechaHoraSolicitud.required ? "*" : ""
                      }`}
                    disableFuture
                    inputVariant="outlined"
                    sx={StyledCommonForms.input}
                    disabled={disabled || disableDateUpdate}
                    minDate={fechaHoraSolicitudMinDate}
                    autoComplete="off"
                  />
                </StyledCommonForms.LeftColumnContainer>
                <StyledCommonForms.RightColumnContainer>
                  <FormikDateTimePicker
                    name="fechaHoraTurno"
                    label={`Para el ${fields.fechaHoraTurno.required ? "*" : ""}`}
                    inputVariant="outlined"
                    sx={StyledCommonForms.input}
                    disabled={disabled || disableDateUpdate}
                    minDate={
                      values.fechaHoraSolicitud
                        ? values.fechaHoraSolicitud
                        : undefined
                    }
                    maxDate={
                      String(values.idTipoTraslado) === "1"
                        ? moment().endOf("day").toDate()
                        : undefined
                    }
                    autoComplete="off"
                  />
                </StyledCommonForms.RightColumnContainer>
              </StyledCommonForms.RowContainer>
              <StyledCommonForms.RowContainer>
                {!hideTipoTransporte && (
                  <StyledCommonForms.LeftColumnContainer>
                    <FormikSelect
                      name="tipoTransporteNombre"
                      value={formValues.tipoTransporteNombre}
                      label={`Tipo de transporte ${fields.tipoTransporteNombre.required ? "*" : ""
                        }`}
                      variant="outlined"
                      sx={StyledCommonForms.input}
                      disabled={disabled}>
                      {fields.tipoTransporteNombre.options
                        .filter(
                          (ttn) =>
                            String(ttn.id) !== "0" &&
                            !String(ttn.nombre || "")
                              .toLowerCase()
                              .includes("sin transporte"),
                        )
                        .map((ttn) => (
                          <MenuItem value={ttn.id} key={ttn.id}>
                            {ttn.nombre}
                          </MenuItem>
                        ))}
                    </FormikSelect>
                  </StyledCommonForms.LeftColumnContainer>
                )}
                <StyledCommonForms.LeftColumnContainer>
                  <FormikSelect
                    name="tipoUrgenciaNombre"
                    value={formValues.tipoUrgenciaNombre}
                    label={`Tipo de urgencia ${fields.tipoUrgenciaNombre.required ? "*" : ""
                      }`}
                    variant="outlined"
                    sx={StyledCommonForms.input}
                    disabled={disabled}>
                    {fields.tipoUrgenciaNombre.options.map((tun) => (
                      <MenuItem value={tun.id} key={tun.id}>
                        {tun.nombre}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </StyledCommonForms.LeftColumnContainer>
              </StyledCommonForms.RowContainer>
              {(hideSolicitadoPor ? !hideEsperaRetorno : true) && (
                <StyledCommonForms.RowContainer>
                  {!hideSolicitadoPor && (
                    <StyledCommonForms.LeftColumnContainer>
                      <FormikAutocomplete
                        name="solicitadoPor"
                        label={`Solicitado por ${fields.solicitadoPor.required ? "*" : ""
                          }`}
                        variant="outlined"
                        options={fields.solicitadoPor.options}
                        sx={StyledCommonForms.input}
                        disabled={disabled}
                      />
                    </StyledCommonForms.LeftColumnContainer>
                  )}
                  {!hideEsperaRetorno && (
                    <StyledCommonForms.RightColumnContainerCheckBoxStart>
                      <FormikRadio
                        name="esperaRetorno"
                        label={`Espera y retorno ${fields.esperaRetorno.required ? "*" : ""
                          }`}
                        disabled={disabled}
                        row>
                        <FormControlLabel
                          value="Sí"
                          control={<Radio />}
                          label="Sí"
                          labelPlacement="end"
                        />
                        <FormControlLabel
                          value="No"
                          control={<Radio />}
                          label="No"
                          labelPlacement="end"
                        />
                      </FormikRadio>
                    </StyledCommonForms.RightColumnContainerCheckBoxStart>
                  )}
                </StyledCommonForms.RowContainer>
              )}
              {renderTrasladoExtras?.({ values, setFieldValue })}
              <StyledCommonForms.RowContainer>
                <FormikTextFieldSpeech
                  id="observaciones"
                  name="observaciones"
                  label={`Observaciones ${fields.observaciones.required ? "*" : ""
                    }`}
                  type="text"
                  variant="outlined"
                  multiline
                  minRows={3}

                  sx={StyledCommonForms.input}
                  disabled={disabled}
                />
              </StyledCommonForms.RowContainer>
              {renderDirections ? (
                renderDirections({ setFieldValue, values })
              ) : (
                <>
                  <DireccionFpt
                    values={values}
                    isDesde={true}
                    fields={fields}
                    disabled={disabled}
                    setFieldValue={setFieldValue}
                    idArt={idArt}
                    direccionesHabituales={direccionesHabitualesDesde}
                    setDireccionesHabitualesOtro={setDireccionesHabitualesHasta}
                    initialValues={formValues}
                    showMap={false}
                    showSearch
                    lockProvinciaLocalidad={false}
                    enableAutoGeocode={false}
                  />
                  <DireccionFpt
                    values={values}
                    isDesde={false}
                    fields={fields}
                    disabled={disabled}
                    setFieldValue={setFieldValue}
                    idArt={idArt}
                    direccionesHabituales={direccionesHabitualesHasta}
                    setDireccionesHabitualesOtro={setDireccionesHabitualesDesde}
                    initialValues={formValues}
                    showMap={false}
                    showSearch
                    lockProvinciaLocalidad={false}
                    enableAutoGeocode={false}
                  />
                </>
              )}
              {model.configuration.showAttachments && (
                <div>
                  <FormSectionDivider title="Adjuntos" />
                  <StyledCommonForms.RowContainer>
                    <DropZone
                      files={files}
                      setFiles={setFiles}
                      configuration={model.configuration}
                      error={attachmentError}
                      setError={setAttachmentError}
                      disabled={disabled}
                      selectedDocument={selectedDocument}
                      accept={model.configuration.availableExtensions}
                    />
                  </StyledCommonForms.RowContainer>
                </div>
              )}
            </StyledCommonForms.FieldContainer>
            <StyledCommonForms.ActionsContainer>
              <FormikActions handleCancel={handleClose} disabled={disabled} />
            </StyledCommonForms.ActionsContainer>
          </StyledCommonForms.FormContainer>
        ))}
    </FormikForm>
  );
};

export default Fpt;
